import {KeyboardEvent, MouseEvent, useState} from 'react';

import {useTranslation} from 'react-i18next';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {Box, Chip, Collapse, Grid, Typography} from '@mui/material';
import {alpha} from '@mui/material/styles';

import {columnContainerStyles, columnHeaderStyles, paperStyles} from '../BoardViewStyles';

import {KanbanMatchCardContent} from './KanbanMatchCardContent';
import {EmpionButton} from '../../../../../../../components/EmpionButton/EmpionButton';
import {EmpionPaper} from '../../../../../../../components/EmpionPaper/EmpionPaper';
import {empionGreen, empionOrange} from '../../../../../../../layouts/theme/EmpionColors';
import {MatchStatus} from '../../../../../../../types/MatchStatus/MatchStatus';
import {MatchData} from '../../../../../../../types/shared-recruiting-types';

type KanbanHeader = 'new' | 'contacted' | 'interview' | 'inProgress' | 'hired' | 'rejected' | 'onHold';

/** Near-white column backdrops; contacted = light orange, interview = light green. */
const KANBAN_COLUMN_TINT_LIGHT: Record<KanbanHeader, string> = {
  new: '#f3f5f4',
  contacted: alpha(empionOrange[100], 0.1),
  interview: empionGreen[50],
  inProgress: '#f7f4ee',
  hired: '#f2f0ed',
  rejected: '#f7f0eb',
  onHold: '#efeeeb',
};

const acceptedColumnCardShadow =
  '0 2px 8px rgba(9, 44, 76, 0.07), 0 3px 14px rgba(224, 139, 88, 0.16), 0 1px 3px rgba(9, 44, 76, 0.05)';

const acceptedColumnCardShadowHover =
  '0 4px 14px rgba(9, 44, 76, 0.09), 0 5px 20px rgba(224, 139, 88, 0.2), 0 2px 6px rgba(9, 44, 76, 0.06)';

interface Props {
  matches: MatchData[];
  total: number;
  onOpenMatchDetails: (matchId: string) => void;
  header: KanbanHeader;
  onCardDragStart: (matchId: string, fromHeader: KanbanHeader) => void;
  onCardDragEnd: () => void;
  onColumnDragOver: (targetHeader: KanbanHeader) => void;
  onCardDrop: (targetHeader: KanbanHeader) => void;
  /** When header is `inProgress`, map each card to the logical column for drag/drop (contacted vs interview). */
  resolveDragSourceHeader?: (match: MatchData) => 'contacted' | 'interview';
  isDropTarget?: boolean;
  onMoveToContacted?: (matchId: string) => Promise<void> | void;
  onMoveToRejected?: (matchId: string) => Promise<void> | void;
  isStatusUpdating?: boolean;
  canLoadMore?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  /** Use when the column sits inside a flex row (matches board split/merge) instead of a MUI Grid row. */
  flexChild?: boolean;
}

export const KanbanColumn = ({
  matches,
  total,
  onOpenMatchDetails,
  header,
  onCardDragStart,
  onCardDragEnd,
  onColumnDragOver,
  onCardDrop,
  resolveDragSourceHeader,
  isDropTarget,
  onMoveToContacted,
  onMoveToRejected,
  isStatusUpdating = false,
  canLoadMore = false,
  onLoadMore,
  isLoadingMore = false,
  flexChild = false,
}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'companyApplicants.applicantsCard.headers'});
  const {t: tMatches} = useTranslation('translation', {keyPrefix: 'matchesPage'});
  const {t: tColumn} = useTranslation('translation', {keyPrefix: 'matchesPage.kanbanColumn'});
  const [expanded, setExpanded] = useState(true);
  const statusAccentColor: Record<KanbanHeader, string> = {
    new: empionGreen[40],
    contacted: empionOrange[6],
    interview: empionGreen[100],
    inProgress: empionOrange[40],
    hired: empionGreen[50],
    rejected: empionOrange[7],
    onHold: empionOrange[5],
  };
  const isAcceptedColumn = header === 'new';

  const cardAccentColor = (match: MatchData) => {
    if (header !== 'inProgress') {
      return statusAccentColor[header];
    }
    const s = match.status;
    return s === MatchStatus.Interview || s === 'interview'
      ? statusAccentColor.interview
      : statusAccentColor.contacted;
  };

  const toggleExpanded = () => {
    setExpanded((prev) => !prev);
  };

  const handleHeaderClick = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    toggleExpanded();
  };

  const handleHeaderKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      toggleExpanded();
    }
  };

  const outerSx = {
    width: '100%' as const,
    minWidth: 0,
    alignSelf: 'flex-start' as const,
    ...(flexChild ? {flex: {xs: 'none', md: '1 1 0'} as const} : {flex: {md: '1 1 0'} as const}),
  };

  const columnShell = (
    <Box
      sx={{
        ...columnContainerStyles,
        height: 'auto',
        minHeight: 0,
        width: '100%',
        backgroundColor: KANBAN_COLUMN_TINT_LIGHT[header],
        outline: isDropTarget ? `2px dashed ${empionOrange[70]}` : 'none',
        outlineOffset: isDropTarget ? '2px' : '0',
      }}
      onDragOver={(e) => {
        e.preventDefault();
        onColumnDragOver(header);
      }}
      onDrop={() => onCardDrop(header)}
    >
      <Box
        onClick={handleHeaderClick}
        onKeyDown={handleHeaderKeyDown}
        role='button'
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={String(expanded ? tColumn('collapseColumn') : tColumn('expandColumn'))}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: expanded ? 2 : 0,
          cursor: 'pointer',
          userSelect: 'none',
          borderRadius: '8px',
          outline: 'none',
          '&:focus-visible': {
            boxShadow: `0 0 0 2px ${empionGreen[200]}`,
          },
          '&:hover': {
            bgcolor: 'rgba(9, 44, 76, 0.04)',
          },
        }}
      >
        <Box sx={{minWidth: 0, flex: 1, pr: 1}}>
          <Typography
            variant='body1'
            noWrap
            sx={{
              ...columnHeaderStyles,
              fontSize: '16px',
              lineHeight: 1.3,
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            }}
          >
            {t(header)}
          </Typography>
        </Box>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0}}>
          <Chip
            size='small'
            label={total}
            sx={{
              height: 24,
              fontWeight: 700,
              color: empionGreen[700],
              backgroundColor: 'white',
              border: '1px solid rgba(9, 44, 76, 0.08)',
            }}
          />
          <ExpandMoreIcon
            aria-hidden
            sx={{
              fontSize: 22,
              color: empionGreen[100],
              flexShrink: 0,
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s ease',
            }}
          />
        </Box>
      </Box>
      <Collapse
        in={expanded}
        orientation='vertical'
        timeout='auto'
        unmountOnExit
        sx={{width: '100%', display: 'block'}}
      >
        <Box sx={{width: '100%', display: 'flex', flexDirection: 'column'}}>
          {matches.map((match) => (
            <EmpionPaper
              key={match.resourceId}
              data-match-card-id={match.resourceId}
              elevation={0}
              draggable
              onDragStart={() =>
                onCardDragStart(
                  match.resourceId,
                  resolveDragSourceHeader ? resolveDragSourceHeader(match) : header
                )
              }
              onDragEnd={onCardDragEnd}
              sx={{
                borderTop: `4px solid ${cardAccentColor(match)}`,
                ...paperStyles,
                ...(isAcceptedColumn && {
                  boxShadow: acceptedColumnCardShadow,
                  '&:hover': {
                    boxShadow: acceptedColumnCardShadowHover,
                  },
                }),
              }}
              onClick={() => onOpenMatchDetails(match.resourceId)}
            >
              <KanbanMatchCardContent
                match={match}
                onMoveToContacted={isAcceptedColumn ? onMoveToContacted : undefined}
                onMoveToRejected={onMoveToRejected}
                isStatusUpdating={isStatusUpdating}
              />
            </EmpionPaper>
          ))}
          {canLoadMore && onLoadMore && matches.length > 0 && (
            <EmpionButton
              variant='secondary'
              onClick={onLoadMore}
              disabled={isLoadingMore}
              sx={{
                width: '100%',
                height: 36,
                mt: 0.5,
                fontSize: '14px',
                lineHeight: 1.5,
                borderColor: empionOrange[40],
              }}
            >
              {String(tMatches('loadMore'))}
            </EmpionButton>
          )}
        </Box>
      </Collapse>
    </Box>
  );

  if (flexChild) {
    return (
      <Box sx={outerSx}>
        {columnShell}
      </Box>
    );
  }

  return (
    <Grid item width={{xs: '100%', md: 'auto'}} minWidth={0} sx={outerSx}>
      {columnShell}
    </Grid>
  );
};
